import { Fragment, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { isByDesign, sentenceOf } from '../api/errors';
import type { ApiFailure } from '../api/errors';
import { useT } from '../messages';
import type { T } from '../messages';

/**
 * The small pieces every screen uses, and the three that carry a rule.
 *
 *  * **`Failure`** renders `501 unsupported` differently from everything else,
 *    because it is not a fault (see `api/errors.ts`).
 *  * **`Confirm`** exists so that no lifecycle move is a single click, and it
 *    quotes the consequence rather than asking "are you sure?".
 *  * **`OnceBox`** shows a value that will never be shown again, says so in the
 *    CLI's own words, and drops it from memory when dismissed.
 */

/* ------------------------------------------------------------------ */
/* Catalog sentences that contain markup                               */
/* ------------------------------------------------------------------ */

const MARKUP = /(`[^`]+`|\*\*[^*]+\*\*)/g;

/**
 * A translated sentence with identifiers in it.
 *
 * Console copy is full of them — `` `admin_api` ``, `` `pending_dns` ``,
 * `` `DELETE` `` — and the alternative is to split every such sentence into
 * "the part before the code" and "the part after", which freezes **English word
 * order** into the catalog: a Spanish sentence that wanted the identifier
 * earlier could not have it. So the markers travel inside the entry and this
 * turns them into elements: `` `like this` `` becomes `<code>` and
 * `**like this**` becomes `<strong>`.
 *
 * Two markers and no more. This is not a Markdown renderer and must not grow
 * into one — a link needs an `href`, which is a prop rather than a marker, and
 * the one sentence in the catalog with an anchor in the middle is deliberately
 * two entries that say so.
 *
 * There is no `dangerouslySetInnerHTML` here and there will not be: every piece
 * below is a React child, so the bytes are text whatever they contain.
 */
export function Rich({ text }: { text: string }): ReactNode {
  const pieces = text.split(MARKUP);
  return (
    <>
      {pieces.map((piece, i) => {
        const key = `${String(i)}:${piece}`;
        if (piece.startsWith('`') && piece.endsWith('`') && piece.length > 1) {
          return <code key={key}>{piece.slice(1, -1)}</code>;
        }
        if (piece.startsWith('**') && piece.endsWith('**') && piece.length > 3) {
          return <strong key={key}>{piece.slice(2, -2)}</strong>;
        }
        return <Fragment key={key}>{piece}</Fragment>;
      })}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Status                                                              */
/* ------------------------------------------------------------------ */

/**
 * A job run's state as a coloured word. The token picks the colour; the word
 * is the operator's (plan §1, principle 2). A state a newer server sends that
 * this build has no word for is shown as its token rather than as nothing.
 */
export function StatePill({ state }: { state: string }): ReactNode {
  const t = useT();
  return <span className={`pill pill--job-${state}`}>{jobStateWord(t, state)}</span>;
}

function jobStateWord(t: T, state: string): string {
  switch (state) {
    case 'running':
      return t('jobs.state.running');
    case 'succeeded':
      return t('jobs.state.succeeded');
    case 'failed':
      return t('jobs.state.failed');
    case 'unknown':
      return t('jobs.state.unknown');
    default:
      return state;
  }
}

/* ------------------------------------------------------------------ */
/* Failure                                                             */
/* ------------------------------------------------------------------ */

/**
 * What the server said, in the server's words.
 *
 * `admin-api` writes real sentences — "products, certificates still depend on
 * sales", "Two keys are already active … revoke one first" — and puts the
 * instruction in `detail.hint`. Paraphrasing them here would produce a console
 * that is vaguer than the CLI, so the rule is: show the message, show the hint,
 * show the list the refusal named, and add nothing.
 *
 * **That rule survives i18n unchanged, and it is the reason a Spanish console
 * still prints an English refusal.** `failure.message`, `failure.hint` and
 * `failure.list` are Domain C — generated by the service that knows what
 * happened, in the words the CLI prints and the log carries — so they pass
 * through untranslated. What *is* translated is the framing this component adds
 * around them: the heading below and the rule paragraph beside it.
 *
 * **The rule cuts the other way too.** Three of the failures a screen can be
 * handed were never written by a service — the fetch did not land, the edge
 * answered with something that is not one of our error bodies, or the body did
 * not match the schema — and those sentences are the console's own, so they
 * come from the catalog like every other word it writes. `sentenceOf` decides
 * which of the two a given failure is; `en.ts` names the three.
 *
 * The exception is `unsupported`. That code means a screen asked for something
 * the `admin_api` role holds no grant on — spec §6.3's "never a data browser"
 * answering from underneath the service — and it renders as a statement of the
 * design, not as an error.
 */
export function Failure({ failure }: { failure: ApiFailure }): ReactNode {
  const t = useT();

  if (isByDesign(failure)) {
    return (
      <div className="by-design" role="note">
        <h4>{t('failure.byDesign.title')}</h4>
        {/* The server's sentence, verbatim. */}
        <p>{failure.message}</p>
        <p className="by-design__rule">
          <Rich text={t('failure.byDesign.rule')} />
        </p>
      </div>
    );
  }

  return (
    <div className="failure" role="alert">
      <p className="failure__message">
        {/* The server's sentence verbatim — unless the console minted it, in
            which case it is Domain A and comes from the catalog. `sentenceOf`
            is the whole of that distinction. */}
        <span className="failure__code">{failure.code}</span> {sentenceOf(failure, t)}
      </p>
      {failure.hint !== null ? <p className="failure__hint">{failure.hint}</p> : null}
      {failure.list.length > 0 ? (
        <ul className="failure__list">
          {failure.list.map((item) => (
            <li key={item}>
              <code>{item}</code>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Confirm                                                             */
/* ------------------------------------------------------------------ */

/**
 * A confirm that quotes the consequence.
 *
 * "Are you sure?" is a dialog people learn to click through. What an operator
 * needs before flipping a tenant to `live` is the sentence saying that from
 * that moment an agent will promise guests times on our word — so the
 * consequence is the body of the dialog, and the button says the verb rather
 * than "OK".
 *
 * `title`, `consequence` and `confirmLabel` arrive already translated: the
 * caller is the screen that knows *which* consequence this is, and half of them
 * are keyed by a status token this component has never heard of. The
 * consequence goes through `Rich`, because half of them name an identifier.
 *
 * It behaves like a dialog rather than a box that happens to float
 * (console redesign, phase A):
 *
 *  * **Focus starts on Cancel.** The safe answer is the one Enter gives; the
 *    verb has to be reached on purpose.
 *  * **Focus stays inside.** Tab and Shift+Tab cycle the dialog's own controls,
 *    so a keyboard cannot wander onto the page underneath and act on it while
 *    the question is open.
 *  * **Esc is Cancel**, except while the request is in flight — a cancel then
 *    would hide a write that is still going to land.
 *  * **Focus goes back** to whatever opened it, so a keyboard operator is not
 *    dropped at the top of the page after answering.
 *
 * `tone` is `danger` by default because that is what every existing caller
 * asks: a status move, a revoke, an import that writes. `primary` is for the
 * consequential-but-ordinary write (a stage move forward, a save on a live
 * business) where red would teach an operator that red means nothing.
 */
export function Confirm({
  title,
  consequence,
  confirmLabel,
  busy,
  failure,
  onConfirm,
  onCancel,
  tone = 'danger',
}: {
  title: string;
  consequence: string;
  confirmLabel: string;
  busy: boolean;
  failure: ApiFailure | null;
  onConfirm: () => void;
  onCancel: () => void;
  tone?: 'danger' | 'primary';
}): ReactNode {
  const t = useT();
  const panel = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  // Read on every render so the Esc handler below never closes over a stale
  // `busy` or a stale `onCancel` from the render that mounted it.
  const latest = useRef({ busy, onCancel });
  latest.current = { busy, onCancel };

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancel.current?.focus();
    return () => {
      // Only if it is still in the page: the opener may have been a menu item
      // that closed on the click that opened this.
      if (opener !== null && opener.isConnected) opener.focus();
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!latest.current.busy) latest.current.onCancel();
      return;
    }
    if (e.key !== 'Tab' || panel.current === null) return;
    const focusable = [
      ...panel.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) {
      e.preventDefault();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="modal">
      <div
        ref={panel}
        className="modal__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={onKeyDown}
      >
        <h3>{title}</h3>
        <p className="modal__consequence">
          <Rich text={consequence} />
        </p>
        {failure !== null ? <Failure failure={failure} /> : null}
        <div className="modal__actions">
          <button type="button" ref={cancel} className="button" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className={`button ${tone === 'danger' ? 'button--danger' : 'button--primary'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? t('common.working') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The once-only box                                                   */
/* ------------------------------------------------------------------ */

/**
 * A value that exists here and nowhere else.
 *
 * The plaintext of a key crosses the wire once (K5); only its SHA-256 hash is
 * stored, so it cannot be recovered. This box holds it in component state, in
 * the CLI's own words, and **dismissing it drops it** — the parent clears the
 * value, so there is nothing left to re-render. There is deliberately no
 * "show again": a console that could show it again would be a console that kept
 * it somewhere.
 *
 * `note` is the server's own `plaintext_note` and prints verbatim, for
 * `Failure`'s reason: it is the sentence the CLI prints when it issues the same
 * key, and two wordings of one instruction is one wording too many.
 */
export function OnceBox({
  label,
  value,
  note,
  onDismiss,
}: {
  label: string;
  value: string;
  note: string;
  onDismiss: () => void;
}): ReactNode {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const copy = (): void => {
    // `navigator.clipboard` is absent in jsdom and over plain http. Selecting
    // the text is the fallback, and the value is on screen either way — the
    // copy button is a convenience, never the only way to get the key.
    void navigator.clipboard
      ?.writeText(value)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  return (
    <div className="once" role="alert">
      <h4>{label}</h4>
      <code className="once__value">{value}</code>
      <p className="once__note">{t('once.note')}</p>
      <p className="once__server-note">{note}</p>
      <div className="once__actions">
        <button type="button" className="button" onClick={copy}>
          {copied ? t('once.copied') : t('once.copy')}
        </button>
        <button type="button" className="button button--primary" onClick={onDismiss}>
          {t('once.stored')}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small things                                                        */
/* ------------------------------------------------------------------ */

/**
 * A moment, in **UTC, always** — and that is the one deliberate exception to
 * "every date goes through `Intl` in the active locale".
 *
 * An operator reading this column is comparing a delivery attempt against a log
 * line, an execution against a Cloud Logging timestamp, or a key's last use
 * against an incident timeline. Every one of those is UTC, and rendering the
 * same instant in whatever zone the laptop happens to be in would make the
 * comparison an arithmetic problem performed under pressure. The `title` and
 * `dateTime` carry the full ISO string for anyone who wants the rest of it.
 *
 * `empty` arrives already translated — "not yet", "running" — because the
 * caller is the screen that knows which absence this is; the default is an em
 * dash, which is the same mark in every language.
 */
export function When({ iso, empty = '—' }: { iso: string | null; empty?: string }): ReactNode {
  if (iso === null) return <span className="muted">{empty}</span>;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return <span className="muted">{iso}</span>;
  return (
    <time dateTime={iso} title={iso}>
      {parsed.toISOString().replace('T', ' ').slice(0, 16)}Z
    </time>
  );
}

/**
 * A labelled control. The hint goes through `Rich` — and so does `Section`'s
 * note below — because the catalog writes identifiers into both, and for a
 * while every one of them printed its backticks and asterisks literally
 * (console redesign, phase A). Plain text passes through `Rich` unchanged, so
 * doing it here, once, is cheaper than remembering it at forty call sites.
 */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint === undefined ? null : (
        <span className="field__hint">
          <Rich text={hint} />
        </span>
      )}
    </label>
  );
}

export function Section({
  title,
  note,
  actions,
  children,
}: {
  title: string;
  note?: string;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="card">
      <header className="card__head">
        <h3>{title}</h3>
        {actions}
      </header>
      {note === undefined ? null : (
        <p className="card__note">
          <Rich text={note} />
        </p>
      )}
      {children}
    </section>
  );
}

/** Loading is a word, not a spinner: an operator read is one round trip. */
export function Loading({ what }: { what: string }): ReactNode {
  const t = useT();
  return <p className="muted">{t('common.reading', { what })}</p>;
}

export function Empty({ children }: { children: ReactNode }): ReactNode {
  return <p className="empty">{children}</p>;
}
